import { useQuery } from '@tanstack/vue-query';
import { USER_ROLES } from '~/constants/auth';

const authFetch = <T>(url: string) => {
  const authStore = useAuthStore();
  return $fetch<T>(url, {
    headers: { Authorization: `Bearer ${authStore.token}` },
  });
};

export const useAgentMe = () => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: ['agent', 'me'],
    queryFn: () => authFetch<any>('/api/agents/me'),
    enabled: computed(() => !!authStore.token),
    select: (res: any) => res?.data ?? null,
    throwOnError: false,
    retry: false,
  });
};

// Agent-write endpoints (referrals, agent-deals, leaderboard etc.) still
// reject LEAD via getAgentUser. Keep this private gate for the queries that
// read agent-only state — but the dashboard read endpoints have been
// relaxed and no longer need it.
const canFetchAgentStats = (role: USER_ROLES | undefined): boolean =>
  role === USER_ROLES.AGENT || role === USER_ROLES.ADMIN || role === USER_ROLES.ACCOUNT_MANAGER;

export const useAgentStats = () => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: ['agent', 'stats'],
    queryFn: () => authFetch<any>('/api/agents/stats'),
    enabled: computed(() => !!authStore.token),
    select: (res: any) => res?.data ?? null,
    staleTime: 30_000,
    throwOnError: false,
    retry: false,
  });
};

export type AgentReferralStats = {
  totalReferrals: number;
  activeReferrals: number;
  totalEarningsAed: number;
  monthlyReferralEarningsAed: number;
  pendingEarningsAed: number;
};

export const useAgentReferralStats = () => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: ['agent', 'referrals', 'stats'],
    queryFn: () => authFetch<{ data: AgentReferralStats }>('/api/agent/referrals/stats'),
    enabled: computed(() => !!authStore.token && canFetchAgentStats(authStore.user?.role)),
    select: (res) => res?.data,
    staleTime: 30_000,
    throwOnError: false,
    retry: false,
  });
};

export type AgentReferralListRow = {
  id: string;
  agentProfileId: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  email: string;
  joinedAt: string;
  status: 'ACTIVE' | 'PENDING';
  dealsClosed: number;
  totalEarningsAed: number;
  monthlyEarningsAed: number;
  agentReference: string | null;
};

export type AgentReferralDealBreakdownRow = {
  id: string;
  dealId: string;
  dealNumber: string;
  sourceType: 'DEAL_REGISTRATION' | 'AGENT_DEAL' | 'UNKNOWN';
  closedAt: string;
  payoutStatus: 'PENDING' | 'PAID';
  referralStatus: 'PENDING' | 'CONVERTED' | 'REJECTED';
  payoutAmountAed: number;
  referralPercentApplied: number;
  dicMarginAmountAed: number;
  dealCommissionAmountAed: number;
  formulaText: string | null;
};

export type AgentReferralDealBreakdown = {
  referredAgent: {
    id: string;
    fullName: string;
    email: string;
    agentReference: string;
  };
  summary: {
    dealsCount: number;
    pendingDealsCount: number;
    paidDealsCount: number;
    totalEarningsAed: number;
    paidEarningsAed: number;
    pendingEarningsAed: number;
    referralPercents: number[];
    isStaticPercent: boolean;
  };
  deals: AgentReferralDealBreakdownRow[];
};

type AgentReferralListResponse = {
  rows: AgentReferralListRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export const useAgentReferrals = () => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: ['agent', 'referrals', 'list'],
    queryFn: () => authFetch<{ data: AgentReferralListResponse }>('/api/agent/referrals'),
    enabled: computed(() => !!authStore.token && canFetchAgentStats(authStore.user?.role)),
    select: (res) => res?.data,
    staleTime: 30_000,
    throwOnError: false,
    retry: false,
  });
};

export const useAgentReferralDealBreakdown = (agentProfileId: MaybeRef<string | null>) => {
  const authStore = useAuthStore();
  const profileIdRef = computed(() => unref(agentProfileId));

  return useQuery({
    queryKey: computed(() => ['agent', 'referrals', 'details', profileIdRef.value]),
    queryFn: () =>
      authFetch<{ data: AgentReferralDealBreakdown }>(
        `/api/agent/referrals/${encodeURIComponent(profileIdRef.value as string)}/deals`,
      ),
    enabled: computed(
      () => !!authStore.token && canFetchAgentStats(authStore.user?.role) && !!profileIdRef.value,
    ),
    select: (res) => res?.data,
    staleTime: 30_000,
    throwOnError: false,
    retry: false,
  });
};

export const useAgentDeals = (filters?: Ref<Record<string, string>>) => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: ['agent', 'deals', filters],
    queryFn: () => {
      const params = new URLSearchParams(filters?.value || {});
      return authFetch<any>(`/api/agents/deals?${params}`);
    },
    enabled: computed(() => !!authStore.token),
    select: (res: any) => res?.data ?? null,
    throwOnError: false,
    retry: false,
  });
};

export const useAgentLeaderboard = () => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: ['agent', 'leaderboard'],
    queryFn: () => authFetch<any>('/api/agents/leaderboard'),
    enabled: computed(() => !!authStore.token),
    select: (res: any) => res?.data ?? null,
    staleTime: 60_000,
    throwOnError: false,
    retry: false,
  });
};
